import React from 'react';
import { Link } from 'react-router-dom';

import { useVmaRecord, libelleRecord, InfoVma } from '../Preferences/VmaRecord.tsx';
import SeancesDuJour from './SeancesDuJour.tsx';
import { lireJournal, lireKilometrage, type ActiviteRealisee, type Journal, type Kilometrage, type KilometrageTotal } from '../Journal/journalApi.ts';
import { estNoteJournee, type SeancePlanifiee } from '../Planning/planningApi.ts';
import { ajouterJours, formaterKm, iso } from '../Planning/dates.ts';
import { formaterDuree } from '../Construction/formatDuree.ts';
import { chargerClub, useMoi, type Club } from '../Auth/moi.ts';

type TileData = {
  id: number;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  value?: React.ReactNode;
};

const tiles: TileData[] = [
];

const containerStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridAutoRows: 'auto',
  gap: 16,
  padding: 16,
  maxWidth: 960,
  margin: '0 auto',
};

const joursStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  // Colonnes de meme hauteur (cf SeancesDuJour).
  alignItems: 'stretch',
  gap: 16,
  padding: '0 16px',
  maxWidth: 960,
  margin: '0 auto',
};

const tileStyle: React.CSSProperties = {
  background: 'var(--fond-tuile-doux)',
  border: '1px solid var(--bord-tuile)',
  borderRadius: 8,
  padding: 12,
  boxShadow: '0 1px 3px rgba(16,24,40,0.05)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 100,
};

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--texte)',
  marginBottom: 8,
  fontWeight: 600,
};

const valueStyle: React.CSSProperties = {
  fontSize: 20,
  color: 'var(--texte)',
  fontWeight: 700,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--texte-secondaire)',
  marginTop: 6,
};

function formaterTotalKm(t: KilometrageTotal): string {
  return `${Math.round(t.distanceM / 1000).toLocaleString('fr-FR')} km`;
}

function sorties(n: number): string {
  return `${n} sortie${n > 1 ? 's' : ''}`;
}

// Lundi de la semaine de d.
function lundi(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() - ((r.getDay() + 6) % 7));
  return r;
}

type TotalSemaine = {
  duree: number;
  distance: number;
  nbActivites: number;
  dureePrevue: number;
  distancePrevue: number;
  // Seances prevues (hors repos) jusqu'a aujourd'hui, et celles realisees.
  nbEchues: number;
  nbRealisees: number;
  nbPrevues: number;
};

// Totaux realise / prevu d'une semaine (meme calcul que le journal).
function totalSemaine(journal: Journal, debut: string, fin: string, aujourdhui: string): TotalSemaine {
  const dans = (date: string) => date >= debut && date <= fin;
  const faites = journal.activites.filter((a: ActiviteRealisee) => dans(a.date));
  const prevues = journal.planifiees.filter((p: SeancePlanifiee) => dans(p.date) && !estNoteJournee(p.categorie));
  const realisees = new Set(journal.activites.map((a: ActiviteRealisee) => a.idPlanifiee).filter(Boolean));
  const echues = prevues.filter((p: SeancePlanifiee) => p.date <= aujourdhui);
  return {
    duree: faites.reduce((t: number, a: ActiviteRealisee) => t + (a.dureeS ?? 0), 0),
    distance: faites.reduce((t: number, a: ActiviteRealisee) => t + (a.distanceM ?? 0), 0),
    nbActivites: faites.length,
    dureePrevue: prevues.reduce((t: number, p: SeancePlanifiee) => t + (p.dureeTotaleS ?? 0), 0),
    distancePrevue: prevues.reduce((t: number, p: SeancePlanifiee) => t + (p.distanceTotaleM ?? 0), 0),
    nbEchues: echues.length,
    nbRealisees: echues.filter((p: SeancePlanifiee) => realisees.has(p.id)).length,
    nbPrevues: prevues.length,
  };
}

const cellule: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };
const entete: React.CSSProperties = { fontSize: 11, color: 'var(--texte-secondaire)', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 500 };

// Realise / prevu cote a cote : duree puis km.
function RealisePrevu({ t }: { t: TotalSemaine }) {
  const ou = (v: string) => v || '—';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12, rowGap: 2, alignItems: 'baseline' }}>
      <span style={entete}>Réalisé</span>
      <span style={entete}>Prévu</span>
      <span style={{ ...cellule, ...valueStyle }}>{ou(t.duree ? formaterDuree(t.duree) : '')}</span>
      <span style={{ ...cellule, fontSize: 16, fontWeight: 600, color: 'var(--texte-secondaire)' }}>{ou(t.dureePrevue ? formaterDuree(t.dureePrevue) : '')}</span>
      <span style={{ ...cellule, fontSize: 15, fontWeight: 600 }}>{ou(formaterKm(t.distance))}</span>
      <span style={{ ...cellule, fontSize: 14, color: 'var(--texte-secondaire)' }}>{ou(formaterKm(t.distancePrevue))}</span>
    </div>
  );
}

function bilanSeances(t: TotalSemaine, enCours: boolean): string {
  const activites = `${t.nbActivites} activité${t.nbActivites > 1 ? 's' : ''}`;
  if (t.nbPrevues === 0) return activites;
  const reste = enCours && t.nbPrevues > t.nbEchues ? ` · ${t.nbPrevues - t.nbEchues} à venir` : '';
  return `${activites} · ${t.nbRealisees}/${t.nbEchues} séance${t.nbEchues > 1 ? 's' : ''} prévue${t.nbEchues > 1 ? 's' : ''} réalisée${t.nbRealisees > 1 ? 's' : ''}${reste}`;
}

// Athlete : son club et son coach (il n'a pas acces a la page Club).
function useTuileClubAthlete(): TileData | null {
  const moi = useMoi();
  const athlete = moi?.role === 'ATHLETE';
  const idClub = moi?.idClub;
  const [club, setClub] = React.useState<Club | null | undefined>(undefined);
  const [erreur, setErreur] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!athlete || !idClub) return;
    chargerClub()
      .then(setClub)
      .catch((e: Error) => setErreur('Club indisponible : ' + e.message));
  }, [athlete, idClub]);
  if (!athlete) return null;

  const moiMembre = club?.membres.find((m) => m.moi);
  const coach = club?.membres.find((m) => m.id === moiMembre?.idCoachSuivi);
  const nomCoach = coach ? (coach.nom || coach.email) : moiMembre?.coachSuivi ?? moi?.coach ?? null;
  const autresCoachs = club ? club.membres.filter((m) => m.coach && !m.enAttente && m.id !== coach?.id).length : 0;
  const admins = club ? club.membres.filter((m) => m.administrateur && !m.enAttente) : [];

  return {
    id: 5,
    title: <>Mon club</>,
    value: !idClub ? 'Aucun club' : club ? club.nom : erreur ? '—' : '…',
    subtitle: erreur ?? (
      <>
        <div>
          {nomCoach
            ? <>Coach : {nomCoach}{coach && <> · <a href={'mailto:' + coach.email} style={{ color: 'inherit' }}>{coach.email}</a></>}</>
            : 'Pas encore de coach'}
        </div>
        {club && !club.personnel && (
          <div>
            {autresCoachs > 0 && <>{autresCoachs} autre{autresCoachs > 1 ? 's' : ''} coach{autresCoachs > 1 ? 's' : ''} · </>}
            {admins.length > 0 && <>Responsable{admins.length > 1 ? 's' : ''} : {admins.map((m) => m.nom || m.email).join(', ')}</>}
          </div>
        )}
      </>
    ),
  };
}

export default function Main(): JSX.Element {
  // VMA de reference, deduite du dernier record (meme valeur que sur la page
  // Mes preferences).
  const { vma, record, chargement, erreur } = useVmaRecord();

  // Kilometrage de course (activites intervals.icu conservees par le serveur).
  const [km, setKm] = React.useState<Kilometrage | null>(null);
  const [erreurKm, setErreurKm] = React.useState<string | null>(null);
  React.useEffect(() => {
    lireKilometrage()
      .then(setKm)
      .catch((e: Error) => setErreurKm('Kilométrage indisponible : ' + e.message));
  }, []);
  const sansCompte = km && !km.compteConfigure && km.total12Mois.nbSorties === 0;
  const lienPreferences = <Link to="/preferences?onglet=intervals">Relier ton compte intervals.icu</Link>;
  const valeurKm = (t?: KilometrageTotal) => (t ? formaterTotalKm(t) : erreurKm ? '—' : '…');
  const evolution = km && km.totalAnneePrecedente.distanceM > 0
    ? Math.round((100 * (km.total12Mois.distanceM - km.totalAnneePrecedente.distanceM)) / km.totalAnneePrecedente.distanceM)
    : null;

  // Semaine en cours et semaine precedente (journal : realise et prevu).
  const bornes = React.useMemo(() => {
    const l = lundi(new Date());
    return {
      debutPrecedente: iso(ajouterJours(l, -7)),
      finPrecedente: iso(ajouterJours(l, -1)),
      debut: iso(l),
      fin: iso(ajouterJours(l, 6)),
      aujourdhui: iso(new Date()),
    };
  }, []);
  const [journal, setJournal] = React.useState<Journal | null>(null);
  const [erreurJournal, setErreurJournal] = React.useState<string | null>(null);
  React.useEffect(() => {
    lireJournal(bornes.debutPrecedente, bornes.fin)
      .then(setJournal)
      .catch((e: Error) => setErreurJournal('Totaux indisponibles : ' + e.message));
  }, [bornes]);
  const semaine = journal ? totalSemaine(journal, bornes.debut, bornes.fin, bornes.aujourdhui) : null;
  const precedente = journal ? totalSemaine(journal, bornes.debutPrecedente, bornes.finPrecedente, bornes.aujourdhui) : null;
  const attente = erreurJournal ? '—' : '…';

  const tuileSemaine: TileData = {
    id: 3,
    title: <Link to="/journal" style={{ color: 'inherit', textDecoration: 'none' }}>Cette semaine</Link>,
    value: semaine ? <RealisePrevu t={semaine} /> : attente,
    subtitle: erreurJournal ?? (semaine ? (
      <>
        {bilanSeances(semaine, true)}
        {journal?.avertissement && <div>{journal.avertissement}</div>}
      </>
    ) : undefined),
  };

  const tuileSemainePrecedente: TileData = {
    id: 4,
    title: <Link to="/journal" style={{ color: 'inherit', textDecoration: 'none' }}>Semaine précédente</Link>,
    value: precedente ? <RealisePrevu t={precedente} /> : attente,
    subtitle: erreurJournal ?? (precedente ? bilanSeances(precedente, false) : undefined),
  };

  const tuileAnnee: TileData = {
    id: 1,
    title: <>Kilométrage {km ? km.anneePrecedente : new Date().getFullYear() - 1}</>,
    value: sansCompte ? '—' : valeurKm(km?.totalAnneePrecedente),
    subtitle: erreurKm ?? (sansCompte ? lienPreferences : km ? `${sorties(km.totalAnneePrecedente.nbSorties)} de course` : undefined),
  };

  const tuile12Mois: TileData = {
    id: 2,
    title: <>Kilométrage des 12 derniers mois</>,
    value: sansCompte ? '—' : valeurKm(km?.total12Mois),
    subtitle: erreurKm ?? (sansCompte ? lienPreferences : km ? (
      <>
        {sorties(km.total12Mois.nbSorties)}
        {evolution !== null && <> · {evolution >= 0 ? '+' : ''}{evolution} % par rapport à {km.anneePrecedente}</>}
        {km.avertissement && <div>{km.avertissement}</div>}
      </>
    ) : undefined),
  };

  const tuileClub = useTuileClubAthlete();

  const tuileVma: TileData = {
    id: 0,
    title: <>VMA <InfoVma emplacementParametres={<>de tes <Link to="/preferences" style={{ color: 'inherit', fontWeight: 'bold' }}>préférences</Link></>} /></>,
    value: vma !== null
      ? `${vma.toLocaleString('fr-FR')} km/h`
      : chargement ? '…' : '—',
    subtitle: erreur
      ? erreur
      : vma !== null && record
        ? <>Record : {libelleRecord(record)}</>
        : !chargement && vma === null
          ? <Link to="/allures">Saisir un record pour la calculer</Link>
          : undefined,
  };

  const tuiles = [tuileSemainePrecedente, tuileSemaine, tuileVma, tuileAnnee, tuile12Mois];
  if (tuileClub) tuiles.push(tuileClub);

  return (
    <div style={{ paddingTop: 80 }}>
      {/* Seances d'aujourd'hui et de demain cote a cote (l'une sous l'autre sur petit ecran) */}
      <div style={joursStyle}>
        <SeancesDuJour />
        <SeancesDuJour decalage={1} />
      </div>
      <div style={containerStyle}>
        {[...tuiles, ...tiles].map((t) => (
          <div key={t.id} style={tileStyle}>
            <div>
              <div style={titleStyle}>{t.title}</div>
              <div style={valueStyle}>{t.value}</div>
            </div>
            {t.subtitle ? <div style={subtitleStyle}>{t.subtitle}</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}
